import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getERPClient } from "@/lib/erp/client";
import { itemQueryKeys } from "../../api/itemQueryKeys";
import { VARIANT_CAPABILITY_SCHEMA } from "@/lib/erp/variants";
import type { VariantDraft } from "@/lib/erp/variants";
import { generatePlannedVariantRows } from "../variants/utils";
import { logger } from "@/lib/utils/logger";

interface VariantCreationDeps {
  /** SKU overrides from context */
  skuOverridesByKey?: Record<string, string>;
}

/**
 * Hook that handles creating Item Variants after a template item is saved.
 *
 * Supports:
 * - Explicit variant list (no cartesian product)
 * - Custom SKU overrides with validation
 * - Warn-only conflict detection
 * - Sync or queued creation
 */
export function useVariantCreation(deps: VariantCreationDeps) {
  const queryClient = useQueryClient();

  const maybeCreateVariantsAfterSave = useCallback(
    async (
      savedItem: Record<string, any>,
      draft: VariantDraft,
      isTemplate: boolean,
      selectedIds?: Set<string>,
      skuOverridesByKey?: Record<string, string>,
    ) => {
      if (!isTemplate) return;

      // Only create for template items (never for variants-of another template)
      if (savedItem.variant_of) return;

      const itemName = savedItem.name || savedItem.item_code;
      if (!itemName) {
        logger.error(
          "[useVariantCreation] Cannot create variants: saved item has no name/item_code",
        );
        return;
      }

      // Get SKU overrides from context if not provided
      const skuOverrides =
        skuOverridesByKey ?? deps.skuOverridesByKey ?? {};

      // Generate all planned rows (up to maxTotalVariants)
      const capability = VARIANT_CAPABILITY_SCHEMA[draft.mode];
      const allRows = generatePlannedVariantRows(draft, {
        limit: capability.maxTotalVariants,
        templateCode: itemName,
      });

      // Filter to selected variants
      let selectedRows =
        selectedIds && selectedIds.size > 0
          ? allRows.filter((row) => selectedIds.has(row.id))
          : allRows;

      // If selection filter produced empty results but variants exist in draft,
      // fall back to all rows (stale selection IDs after draft change)
      if (selectedRows.length === 0 && allRows.length > 0) {
        selectedRows = allRows;
      }

      if (selectedRows.length === 0) {
        const confirmed = window.confirm(
          "No variants selected. Do you want to save the template without creating variants?\n\n" +
            "Click OK to save template only, or Cancel to go back and select variants.",
        );
        if (!confirmed) {
          throw new Error("Save cancelled: no variants selected");
        }
        return;
      }

      const validateSkuOverride = (sku: string): string | null => {
        const trimmed = sku.trim();
        if (!trimmed) return "SKU cannot be empty";
        if (trimmed.length > 140) return "SKU cannot exceed 140 characters";
        if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
          return "SKU can only contain letters, numbers, hyphens, and underscores";
        }
        return null;
      };

      // Build explicit variant list with SKU overrides
      const variants = selectedRows.map((row) => {
        const variantDef: {
          attributes: Record<string, string>;
          item_code?: string;
        } = {
          attributes: row.attributesByName || {},
        };

        const skuOverride = skuOverrides[row.id];
        if (skuOverride && skuOverride.trim()) {
          variantDef.item_code = skuOverride.trim();
        }

        return variantDef;
      });

      // Validate SKU overrides (fail closed) and detect duplicates
      const requestedSkus = variants
        .map((v) => v.item_code)
        .filter((sku): sku is string => !!sku);

      if (requestedSkus.length > 0) {
        const errors: string[] = [];
        const seen = new Map<string, number>();
        for (let i = 0; i < requestedSkus.length; i++) {
          const sku = requestedSkus[i];
          const err = validateSkuOverride(sku);
          if (err) {
            errors.push(`${sku}: ${err}`);
          }
          const prevIdx = seen.get(sku);
          if (prevIdx !== undefined) {
            errors.push(
              `Duplicate SKU "${sku}" used multiple times in selection`,
            );
          } else {
            seen.set(sku, i);
          }
        }

        if (errors.length > 0) {
          alert(
            `Please fix SKU errors before saving:\n${errors.join("\n")}`,
          );
          throw new Error("SKU override validation failed");
        }
      }

      // Conflict check for custom SKUs
      if (requestedSkus.length > 0) {
        try {
          const conflictCheck = await getERPClient().call<{
            existing: string[];
            missing: string[];
          }>(
            "zaviago_backend.api.item_variants.check_item_codes_exist",
            { item_codes_json: JSON.stringify(requestedSkus) },
          );

          if (
            conflictCheck.existing &&
            conflictCheck.existing.length > 0
          ) {
            const confirmed = window.confirm(
              `Warning: ${conflictCheck.existing.length} item code(s) already exist:\n` +
                `${conflictCheck.existing.slice(0, 5).join(", ")}${conflictCheck.existing.length > 5 ? "..." : ""}\n\n` +
                `Backend will auto-append numbers to avoid conflicts.\n\n` +
                `Continue anyway?`,
            );
            if (!confirmed) {
              throw new Error("Save cancelled: item code conflicts");
            }
          }
        } catch (error) {
          // If conflict check fails, proceed anyway (backend will handle)
          logger.warn(
            "[useVariantCreation] Conflict check failed, proceeding:",
            error,
          );
        }
      }

      // Create variants via API
      try {
        const result = await getERPClient().call<{
          status: "created" | "queued";
          created?: Array<{
            requested_item_code: string;
            final_item_code: string;
            name: string;
          }>;
          warnings?: string[];
          count: number;
        }>(
          "zaviago_backend.api.item_variants.create_variants_explicit",
          {
            template_item_code: itemName,
            variants_json: JSON.stringify(variants),
            use_template_image: !!draft.useTemplateImage,
          },
        );

        if (result.status === "created") {
          logger.log(
            `[useVariantCreation] Created ${result.count} variants synchronously`,
          );
          if (result.warnings && result.warnings.length > 0) {
            logger.warn(
              "[useVariantCreation] Variant creation warnings:",
              result.warnings,
            );
            toast.warning(
              `Created ${result.count} variant${result.count !== 1 ? "s" : ""} with warnings`,
              { description: result.warnings.join(", ") },
            );
          } else {
            const dedupNote =
              result.count < variants.length
                ? ` (${variants.length - result.count} already existed)`
                : "";
            toast.success(
              `Created ${result.count} variant${result.count !== 1 ? "s" : ""}${dedupNote}`,
            );
          }
        } else if (result.status === "queued") {
          logger.log(
            `[useVariantCreation] Variant creation queued (${result.count} variants will be created in background)`,
          );
          toast.info(
            `${result.count} variant${result.count !== 1 ? "s" : ""} queued for creation`,
          );
        }

        // Invalidate caches
        queryClient.invalidateQueries({
          queryKey: itemQueryKeys.detail(itemName),
        });
        queryClient.invalidateQueries({
          queryKey: ["doctype-list", "Item"],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: itemQueryKeys.list(),
        });
      } catch (error) {
        logger.error(
          "[useVariantCreation] Failed to create variants:",
          error,
        );
        const errorMsg =
          error instanceof Error ? error.message : String(error);
        toast.error("Variant creation failed", {
          description: `Template saved successfully, but variant creation failed: ${errorMsg}`,
        });
      }
    },
    [queryClient, deps.skuOverridesByKey],
  );

  return maybeCreateVariantsAfterSave;
}
